<template>
  <div :class="{ 'owc-simple-list': true, 'owc-simple-list--single-child': isSingularDownloadView }">
    <template v-if="isEditMode">
      <ValidationBoxWrapper>
        <slot slot="validation-box" name="validation-box" />
      </ValidationBoxWrapper>
      <SlotContainer
        v-for="(item, index) in items"
        :key="index"
        :slotName="`simpleListItem${index}`"
        refName="slot-simple-list-item"
        slot="slot-simple-list-item"
      >
        <slot :slot="`simpleListItem${index}`" :name="`simpleListItem${index}`"></slot>
      </SlotContainer>
      <SlotContainer refName="slot-simple-list" slot="slotSimpleList">
        <slot></slot>
      </SlotContainer>
    </template>
    <template v-else>
      <div v-if="shouldRenderSearchSortControls" class="owc-simple-list__controls">
        <wb-input-control class="owc-simple-list__searchingControl">
          <wb-input>
            <label>{{ searchLabel }}</label>
            <input type="text" v-model="searchPhrase" @keyup.enter="handleSearch" />
            <wb-input-action>
              <button
                v-if="shouldRenderSearchClearButton"
                class="wb-round-button wb-round-button--small"
                type="button"
                aria-label="Clear search"
                ref="searchResetButton"
                @click="resetSearch"
              >
                <wb-icon class="wb-round-button__icon" name="bds/close/24" aria-hidden="true"></wb-icon>
              </button>
              <button
                v-else
                class="wb-round-button wb-round-button--small"
                type="button"
                aria-label="Search"
                ref="searchButton"
                :disabled="!isSearchPhraseLongEnough"
                @click="handleSearch"
              >
                <wb-icon class="wb-round-button__icon" name="bds/search/24" aria-hidden="true"></wb-icon>
              </button>
            </wb-input-action>
          </wb-input>
        </wb-input-control>
        <wb-select-control v-if="shouldRenderSortingControl" class="owc-simple-list__sortingControl" compact>
          <wb-select @change="applyFilterTracking">
            <label>{{ sortByLabel }}</label>
            <select v-model="sortOptionId">
              <option value="">{{ sortByLabel }}</option>
              <option v-for="option in sortOptions" :key="option.id" :value="option.id">{{
                option.label
              }}</option>
            </select>
          </wb-select>
        </wb-select-control>
      </div>
      <div v-if="items.length" class="owc-simple-list__items">
        <SimpleListItem v-for="item in items" :key="item.id" :datajson="buildItemDataJSON(item)" />
      </div>
      <div
        v-if="shouldRenderShowMoreButton"
        class="owc-simple-list__items owc-simple-list__items--hidden"
        aria-hidden="true"
      >
        <SimpleListItem v-for="item in hiddenItems" :key="item.id" :datajson="buildItemDataJSON(item)" />
      </div>
      <div
        v-if="activeSearchPhrase && !items.length"
        v-html="noResultsMessage"
        class="owc-simple-list__noResults"
      ></div>
      <template v-if="shouldRenderShowMoreButton">
        <div class="owc-simple-list__visibleItemsStatus">{{ items.length }} / {{ sourceItems.length }}</div>
        <div class="owc-simple-list__showMore">
          <button
            type="button"
            class="wb-button wb-button--secondary wb-button--large"
            @click="handleShowMore"
          >
            {{ showMoreLabel }}
          </button>
        </div>
      </template>
    </template>
  </div>
</template>

<script lang="ts">
/**
 * TODO: Documentation
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src/ValidationBoxWrapper.vue';
import SimpleListItem from './SimpleListItem.vue';
import { SimpleListComponent, SimpleListItem as SimpleListItemDataJSON } from './SimpleList.d';
import { SimpleListType } from './SimpleList.enums';
import {
  ITEMS_PER_PAGE,
  SORTING_OPTION_KEYS,
  MIN_NUMBER_OF_ITEMS_TO_ENABLE_SORTING,
  MIN_SEARCH_PHRASE_LENGTH,
} from './SimpleList.config';

const SEARCH_ITEM_PROPS_MAP: Partial<Record<SimpleListType, string[]>> = {
  [SimpleListType.Contact]: ['name', 'positionRole', 'location', 'phone', 'email'],
  [SimpleListType.Link]: ['headline', 'description'],
  [SimpleListType.Download]: ['headline', 'fileTitle'],
};
import { TrackingInstance } from '@owc-packages/Tracking/src/Tracking';
import initializeTracking, { formatTrackingString } from '@owc-packages/Tracking/src/TrackingUtils';

let trackingInstance: TrackingInstance | null = null;

export default (ComponentInitializeDispatcher as SimpleListComponent).extend({
  name: 'owc-simple-list',
  mixins: [OwcDataComponent],
  components: {
    SlotContainer,
    ValidationBoxWrapper,
    SimpleListItem,
  },
  data: () => ({
    sortOptionId: '',
    currentPage: 1,
    searchPhrase: '',
    activeSearchPhrase: '',
  }),
  async mounted() {
    trackingInstance = await initializeTracking();
  },
  computed: {
    sourceItems() {
      const items: SimpleListItemDataJSON[] = this.componentData?.items || [];

      if (this.activeSearchPhrase) {
        return items.filter(this.filterOutItemBySearchPhrase);
      }

      return items;
    },
    items() {
      const sortOption = this.findSortOptionById(this.sortOptionId);

      if (!sortOption) {
        return this.getPagedItemsForType(this.sourceItems);
      }

      const { key, label } = sortOption;

      const items = [...this.sourceItems].sort((itemA, itemB) => {
        // Sort by tag
        if (key === SORTING_OPTION_KEYS.tag) {
          return (
            Number(this.getTagsFromItem(itemB).includes(label)) -
            Number(this.getTagsFromItem(itemA).includes(label))
          );
        }

        // Otherwise, sort by item's property name, e.g. location or name
        const propA = itemA[key as keyof SimpleListItemDataJSON];
        const propB = itemB[key as keyof SimpleListItemDataJSON];

        if (typeof propA === 'string' && typeof propB === 'string') {
          return propA.localeCompare(propB);
        }

        return 0;
      });

      return this.getPagedItemsForType(items);
    },
    hiddenItems() {
      return this.getPagedHiddenItemsForType(this.sourceItems);
    },
    sortOptions() {
      return this.componentData && 'sortOptions' in this.componentData ? this.componentData.sortOptions : [];
    },
    sortByLabel() {
      return this.componentData && 'sortByText' in this.componentData ? this.componentData.sortByText : '';
    },
    showMoreLabel() {
      return this.componentData && 'showMoreText' in this.componentData
        ? this.componentData.showMoreText
        : '';
    },
    searchLabel() {
      return this.componentData && 'searchText' in this.componentData ? this.componentData.searchText : '';
    },
    noResultsMessage() {
      return this.componentData && 'noResultsText' in this.componentData
        ? this.componentData.noResultsText
        : '';
    },
    itemsPerPage() {
      return this.componentData?.numberOfShownItems || ITEMS_PER_PAGE;
    },
    shouldRenderSortingControl() {
      if (this.sourceItems.length < MIN_NUMBER_OF_ITEMS_TO_ENABLE_SORTING) {
        return false;
      }
      return Boolean(this.sortOptions.length);
    },
    shouldRenderShowMoreButton() {
      const length = this.sourceItems.length;
      const areThereItemsToBeShown = length > this.currentPage * this.itemsPerPage;
      const areThereMoreItemsThanPerPage = length > this.itemsPerPage;
      return !this.isDownloadList && areThereItemsToBeShown && areThereMoreItemsThanPerPage;
    },
    shouldRenderSearchClearButton() {
      if (this.activeSearchPhrase && !this.isSearchPhraseLongEnough) {
        return true;
      }
      return Boolean(this.activeSearchPhrase && this.isSearchPhraseEqualActive);
    },
    shouldRenderSearchSortControls() {
      return Boolean(this.componentData?.showSearchSort);
    },
    isSingularDownloadView() {
      return this.sourceItems.length === 1 && this.isDownloadList;
    },
    isDownloadList() {
      return this.componentData?.type === SimpleListType.Download;
    },
    isSearchPhraseLongEnough() {
      return this.searchPhrase.length >= MIN_SEARCH_PHRASE_LENGTH;
    },
    isSearchPhraseEqualActive() {
      return this.searchPhrase === this.activeSearchPhrase;
    },
  },
  methods: {
    buildItemDataJSON(item) {
      return JSON.stringify({
        ...item,
        type: this.componentData?.type,
      });
    },
    findSortOptionById(sortOptionId) {
      const option = this.sortOptions.find(sortOption => sortOption.id === sortOptionId);

      if (option && Object.values(SORTING_OPTION_KEYS).includes(option.key)) {
        return option;
      }

      return null;
    },
    getTagsFromItem(item) {
      return 'tags' in item ? item.tags || [] : [];
    },
    handleShowMore() {
      this.currentPage += 1;
    },
    handleSearch() {
      if (this.isSearchPhraseLongEnough) {
        this.activeSearchPhrase = this.searchPhrase;
        this.resetSortAndPaging();
        this.applySearchTracking();
      }
    },
    getPagedItemsForType(items) {
      if (this.isDownloadList) {
        return items;
      }
      return items.slice(0, this.currentPage * this.itemsPerPage);
    },
    getPagedHiddenItemsForType(items) {
      return items.slice(this.currentPage * this.itemsPerPage);
    },
    resetSortAndPaging() {
      this.currentPage = 1;
      this.sortOptionId = '';
    },
    resetSearch() {
      this.searchPhrase = '';
      this.activeSearchPhrase = '';
      this.resetSortAndPaging();
      this.removeSearchTracking();
    },
    filterOutItemBySearchPhrase(item) {
      const listType = this.componentData?.type;
      const searchPhrase = this.activeSearchPhrase.toLowerCase();

      if (!listType) {
        return false;
      }

      for (const prop of SEARCH_ITEM_PROPS_MAP[listType] || []) {
        const value = item[prop as keyof SimpleListItemDataJSON];
        if (value && typeof value === 'string' && value.toLowerCase().includes(searchPhrase)) {
          return true;
        }
      }

      return false;
    },
    issueTracking(label: string, el: Element) {
      if (trackingInstance && this.json) {
        trackingInstance.customTrackingObjectUpdate(
          {
            label,
            category: 'filter',
          },
          'click',
          el,
        );
      }
    },
    applySearchTracking() {
      const keyword = formatTrackingString(this.searchPhrase);
      const numberOfResults = this.sourceItems.length;
      this.issueTracking(`search.${keyword}.${numberOfResults}`, this.$refs.searchButton as Element);
    },
    removeSearchTracking() {
      this.issueTracking('search_removed', this.$refs.searchResetButton as Element);
    },
    applyFilterTracking(event) {
      const activeFilter = event.target as HTMLSelectElement;
      const activeFilterName = activeFilter.options[activeFilter.selectedIndex].text;
      this.issueTracking(`sort.${formatTrackingString(activeFilterName)}`, event.target as Element);
    },
  },
});
</script>

<style lang="scss">
$simpleListControlWidth: 284px;

.owc-simple-list {
  margin: var(--wb-grid-gutter-width) 0;

  @include breakpoint-from(mq3) {
    @include wb-content-width;
  }

  &--single-child {
    grid-template-columns: repeat(1, 1fr);
  }
  &__controls {
    display: flex;
    flex-direction: column;
    margin: 0 0 var(--wb-spacing-s);

    @include wb-content-width;
    @include breakpoint-from(mq3) {
      flex-direction: row;
      justify-content: space-between;
      width: 100%;
    }
  }
  &__searchingControl {
    margin-bottom: var(--wb-spacing-xxs);

    @include breakpoint-from(mq3) {
      margin-bottom: 0;
    }
  }
  &__sortingControl,
  &__searchingControl {
    @include breakpoint-from(mq3) {
      flex: 0 0 $simpleListControlWidth;
    }
  }
  &__items {
    display: grid;
    grid-row-gap: var(--wb-grid-gutter-width);
    grid-column-gap: var(--wb-grid-gutter-width);
    grid-template-columns: repeat(1, 1fr);

    @include breakpoint-from(mq5) {
      grid-template-columns: repeat(2, 1fr);
    }

    &--hidden {
      display: none;
    }
  }
  &__showMore {
    display: flex;
    justify-content: center;
  }
  &__visibleItemsStatus {
    @include wb-type-copy-secondary;

    margin: var(--wb-spacing-s) 0;

    color: var(--wb-grey-45);
    text-align: center;
  }
  &__noResults {
    @include wb-type-copy;
    @include wb-content-width;

    color: var(--wb-grey-45);

    @include breakpoint-from(mq3) {
      width: 100%;
    }
  }
}
</style>
